import { useNavigate } from 'react-router-dom';
import Navbar from '../components/feature/Navbar';

const quickLinks = [
  { icon: 'ri-home-4-line', label: '回到首頁', path: '/', color: 'bg-slate-900 text-white hover:bg-slate-800' },
  { icon: 'ri-user-star-line', label: '找導師', path: '/mentors', color: 'bg-violet-50 text-violet-700 hover:bg-violet-100 border border-violet-200' },
  { icon: 'ri-book-open-line', label: '瀏覽課程', path: '/courses', color: 'bg-violet-50 text-violet-700 hover:bg-violet-100 border border-violet-200' },
  { icon: 'ri-article-line', label: '資源中心', path: '/blog', color: 'bg-violet-50 text-violet-700 hover:bg-violet-100 border border-violet-200' },
  { icon: 'ri-customer-service-2-line', label: '聯絡我們', path: '/contact', color: 'bg-violet-50 text-violet-700 hover:bg-violet-100 border border-violet-200' },
];

export default function NotFound() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#F8F9FC] flex flex-col" style={{ fontFamily: "'Inter', sans-serif" }}>
      <Navbar />

      <div className="flex-1 flex items-center justify-center px-6 py-20">
        <div className="max-w-5xl w-full mx-auto flex flex-col lg:flex-row items-center gap-12 lg:gap-20">

          {/* Left: Text content */}
          <div className="flex-1 text-center lg:text-left order-2 lg:order-1">
            {/* 404 badge */}
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-rose-50 border border-rose-200 rounded-full mb-6">
              <span className="w-1.5 h-1.5 bg-rose-400 rounded-full inline-block animate-pulse" />
              <span className="text-rose-600 text-xs font-semibold tracking-wider uppercase">錯誤 404</span>
            </div>

            <h1 className="text-4xl sm:text-5xl font-black text-[#0A1628] leading-tight mb-4 tracking-tight">
              頁面
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-violet-600 to-indigo-500">
                {' '}迷路了
              </span>
            </h1>

            <p className="text-slate-500 text-base leading-relaxed mb-8 max-w-md mx-auto lg:mx-0">
              你訪問的頁面不存在或已被移除。別擔心，這裡有幾條路可以幫你快速回到正軌。
            </p>

            {/* Quick navigation */}
            <div className="mb-8">
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">快速導覽</p>
              <div className="flex flex-wrap gap-2 justify-center lg:justify-start">
                {quickLinks.map((link) => (
                  <button
                    key={link.path}
                    onClick={() => navigate(link.path)}
                    className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold cursor-pointer transition-all whitespace-nowrap ${link.color}`}
                  >
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className={link.icon} />
                    </span>
                    {link.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Back button */}
            <button
              onClick={() => window.history.length > 1 ? window.history.back() : navigate('/')}
              className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-slate-700 cursor-pointer transition-colors"
            >
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-left-line" />
              </span>
              返回上一頁
            </button>
          </div>

          {/* Right: Illustration */}
          <div className="flex-shrink-0 order-1 lg:order-2 relative">
            {/* Decorative 404 */}
            <div
              className="absolute inset-0 flex items-center justify-center select-none pointer-events-none z-0"
              aria-hidden="true"
            >
              <span
                className="font-black text-[#0A1628]/[0.04] leading-none"
                style={{ fontSize: 'clamp(120px, 20vw, 200px)' }}
              >
                404
              </span>
            </div>

            {/* Illustration */}
            <div className="relative z-10 w-72 h-72 sm:w-80 sm:h-80 lg:w-96 lg:h-96 rounded-3xl overflow-hidden border border-slate-200/80">
              <img
                src="/assets/readdy/fallback-notfound.svg"
                alt="404 迷路了"
                className="w-full h-full object-cover object-top"
              />
              {/* Overlay gradient */}
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A1628]/30 via-transparent to-transparent" />
            </div>

            {/* Floating card 1 */}
            <div className="absolute -bottom-4 -left-4 bg-white rounded-2xl px-4 py-3 border border-slate-100 z-20 shadow-sm">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 flex items-center justify-center bg-amber-50 rounded-lg">
                  <i className="ri-map-pin-2-line text-amber-500 text-sm" />
                </div>
                <div>
                  <p className="text-[10px] text-slate-400 leading-none mb-0.5">你在這裡</p>
                  <p className="text-xs font-bold text-slate-700">未知區域</p>
                </div>
              </div>
            </div>

            {/* Floating card 2 */}
            <div className="absolute -top-4 -right-4 bg-white rounded-2xl px-4 py-3 border border-slate-100 z-20 shadow-sm">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 flex items-center justify-center bg-violet-50 rounded-lg">
                  <i className="ri-compass-3-line text-violet-500 text-sm" />
                </div>
                <div>
                  <p className="text-[10px] text-slate-400 leading-none mb-0.5">導覽中...</p>
                  <p className="text-xs font-bold text-slate-700">尋找目標頁面</p>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>

      {/* Bottom brand strip */}
      <div className="border-t border-slate-100 bg-white py-5 px-6">
        <div className="max-w-5xl mx-auto flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-2.5">
            <img
              src="/assets/readdy/static-358c7e6691b9b58b5179c58a2715558b.jpg"
              alt="NexusGo"
              className="w-6 h-6 rounded-md object-cover"
            />
            <span className="font-bold text-sm text-slate-700">NexusGo</span>
            <span className="text-slate-300">·</span>
            <span className="text-slate-400 text-xs">外商職涯加速平台</span>
          </div>
          <p className="text-slate-400 text-xs">
            如果你認為這是一個錯誤，請
            <button
              onClick={() => navigate('/contact')}
              className="text-violet-600 hover:text-violet-700 cursor-pointer ml-1 font-medium transition-colors"
            >
              聯絡我們
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}
